import { useState, useEffect } from "react";
import { Save, Loader2, RotateCcw, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useContent } from "../../contexts/ContentContext";
import {
  TextField,
  ImageField,
  VideoField,
  ListEditor,
  StringListEditor,
  Toggle,
  SectionHeader,
} from "./EditorFields";

const SECTION_KEYS = [
  { id: "branding", label: "Бренд / Контакты" },
  { id: "home_hero", label: "Hero / Слайдер" },
  { id: "home_pillars", label: "Pillars" },
  { id: "home_popular", label: "Популярные туры" },
  { id: "home_inspire", label: "Inspire / Видео" },
  { id: "home_benchmark", label: "Бенчмарк-компании" },
  { id: "home_why", label: "Почему мы" },
  { id: "home_experience", label: "Опыт" },
  { id: "business", label: "Бизнес-страница" },
  { id: "travel", label: "Travel-страница" },
  { id: "retreat", label: "Retreat" },
  { id: "filming", label: "Съёмки" },
  { id: "contacts", label: "Контакты" },
  { id: "footer", label: "Footer" },
  { id: "visibility", label: "Видимость секций" },
];

const DEFAULT_FILMING_CASES = [
  { title: "RTVI", youtubeUrl: "" },
  { title: "808", youtubeUrl: "" },
  { title: "Pavel Gitelman", youtubeUrl: "" },
  { title: "Production studios", youtubeUrl: "" },
];

export default function ContentEditor() {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [lang, setLang] = useState("ru");
  const [section, setSection] = useState("branding");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  if (!draft) {
    return (
      <div className="bg-midnight-2 border border-white/10 p-12 text-center font-mono text-sm text-white/40">
        Loading...
      </div>
    );
  }

  // helpers to update nested data
  const setLangField = (path, value) => {
    setDraft((prev) => {
      const next = { ...prev };
      next[lang] = { ...prev[lang] };
      let cur = next[lang];
      for (let i = 0; i < path.length - 1; i++) {
        cur[path[i]] = { ...cur[path[i]] };
        cur = cur[path[i]];
      }
      cur[path[path.length - 1]] = value;
      return next;
    });
  };

  const setRoot = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      toast.success("Контент сохранён");
      await refresh();
    } catch (e) {
      toast.error("Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Сбросить весь контент к значениям по умолчанию?")) return;
    setSaving(true);
    try {
      await api.post("/admin/content/reset");
      toast.success("Сброшено к default");
      await refresh();
    } catch {
      toast.error("Не удалось сбросить");
    } finally {
      setSaving(false);
    }
  };

  const tHome = draft[lang]?.home || {};
  const tBus = draft[lang]?.business || {};
  const tTrv = draft[lang]?.travel || {};
  const tRtr = draft[lang]?.retreat || {};
  const tFlm = draft[lang]?.filming || {};
  const tCtc = draft[lang]?.contacts || {};
  const tFt = draft[lang]?.footer || {};

  // ---------- Render section panels ----------

  const renderBranding = () => (
    <div className="space-y-8">
      <SectionHeader title="Глобальные настройки бренда" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <TextField
          label="Название компании"
          value={draft.branding?.company_name}
          onChange={(v) => setRoot("branding", { ...draft.branding, company_name: v })}
        />
        <TextField
          label="Tagline (под логотипом)"
          value={draft.branding?.tagline}
          onChange={(v) => setRoot("branding", { ...draft.branding, tagline: v })}
        />
      </div>
      <ImageField
        label="Логотип"
        value={draft.branding?.logo_url}
        onChange={(v) => setRoot("branding", { ...draft.branding, logo_url: v })}
      />

      <SectionHeader title="Каналы связи" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <TextField
          label="Telegram URL"
          value={draft.channels?.telegram_url}
          onChange={(v) => setRoot("channels", { ...draft.channels, telegram_url: v })}
        />
        <TextField
          label="Telegram handle"
          value={draft.channels?.telegram_handle}
          onChange={(v) => setRoot("channels", { ...draft.channels, telegram_handle: v })}
        />
        <TextField
          label="WhatsApp URL"
          value={draft.channels?.whatsapp_url}
          onChange={(v) => setRoot("channels", { ...draft.channels, whatsapp_url: v })}
        />
        <TextField
          label="WhatsApp номер"
          value={draft.channels?.whatsapp_handle}
          onChange={(v) => setRoot("channels", { ...draft.channels, whatsapp_handle: v })}
        />
        <TextField
          label="WeChat ID"
          value={draft.channels?.wechat_handle}
          onChange={(v) => setRoot("channels", { ...draft.channels, wechat_handle: v })}
        />
        <TextField
          label="Email"
          value={draft.channels?.email}
          onChange={(v) => setRoot("channels", { ...draft.channels, email: v })}
        />
      </div>
    </div>
  );

  const renderHero = () => (
    <div className="space-y-8">
      <SectionHeader title={`Hero — ${lang.toUpperCase()}`} />
      <TextField
        label="Overline (мелкий заголовок сверху)"
        value={tHome.heroOverline}
        onChange={(v) => setLangField(["home", "heroOverline"], v)}
      />
      <ListEditor
        label="Слайды Hero (5 городов)"
        items={tHome.heroSlides}
        addLabel="Добавить слайд"
        fields={[
          { key: "city", label: "Город (внутр. id)" },
          { key: "title", label: "Заголовок (VISIT ...)" },
          { key: "desc", label: "Описание", multiline: true, rows: 2 },
          { key: "image", label: "Фото слайда", type: "image" },
        ]}
        onChange={(v) => setLangField(["home", "heroSlides"], v)}
      />
    </div>
  );

  const renderPillars = () => (
    <div className="space-y-8">
      <SectionHeader title={`Pillars (3 столбца под Hero) — ${lang.toUpperCase()}`} />
      <ListEditor
        items={tHome.pillars}
        addLabel="Добавить столбец"
        fields={[
          { key: "t", label: "Заголовок" },
          { key: "d", label: "Описание", multiline: true, rows: 2 },
          { key: "link", label: "Ссылка (например /business)" },
        ]}
        onChange={(v) => setLangField(["home", "pillars"], v)}
      />
      <TextField
        label="Текст ссылки 'Подробнее'"
        value={tHome.learnMore}
        onChange={(v) => setLangField(["home", "learnMore"], v)}
      />
    </div>
  );

  const renderPopular = () => (
    <div className="space-y-8">
      <SectionHeader title={`Популярные туры — ${lang.toUpperCase()}`} />
      <TextField
        label="Подзаголовок (italic)"
        value={tHome.popularOverline}
        onChange={(v) => setLangField(["home", "popularOverline"], v)}
      />
      <TextField
        label="Заголовок"
        value={tHome.popularTitle}
        onChange={(v) => setLangField(["home", "popularTitle"], v)}
      />
      <ListEditor
        label="Карточки туров"
        items={tHome.popularItems}
        addLabel="Добавить тур"
        fields={[
          { key: "tag", label: "Тег (Тур №X)" },
          { key: "t", label: "Название" },
          { key: "d", label: "Описание (короткое)" },
          { key: "image", label: "Фото", type: "image" },
        ]}
        onChange={(v) => setLangField(["home", "popularItems"], v)}
      />
    </div>
  );

  const renderInspire = () => (
    <div className="space-y-5">
      <SectionHeader title={`Inspire (видео-секция) — ${lang.toUpperCase()}`} />
      <TextField
        label="Overline"
        value={tHome.inspireOverline}
        onChange={(v) => setLangField(["home", "inspireOverline"], v)}
      />
      <TextField
        label="Заголовок (можно несколько строк через \\n)"
        value={tHome.inspireTitle}
        onChange={(v) => setLangField(["home", "inspireTitle"], v)}
        multiline
        rows={3}
      />
      <TextField
        label="Описание"
        value={tHome.inspireDesc}
        onChange={(v) => setLangField(["home", "inspireDesc"], v)}
        multiline
        rows={3}
      />
      <VideoField
        label="�����: YouTube ������ ��� ����"
        value={tHome.inspireVideoUrl}
        onChange={(v) => setLangField(["home", "inspireVideoUrl"], v)}
      />
    </div>
  );

  const renderBenchmark = () => (
    <div className="space-y-8">
      <SectionHeader title={`Бенчмарк-компании — ${lang.toUpperCase()}`} />
      <TextField
        label="Overline"
        value={tHome.benchmarkOverline}
        onChange={(v) => setLangField(["home", "benchmarkOverline"], v)}
      />
      <TextField
        label="Заголовок"
        value={tHome.benchmarkTitle}
        onChange={(v) => setLangField(["home", "benchmarkTitle"], v)}
      />
      <TextField
        label="Описание"
        value={tHome.benchmarkDesc}
        onChange={(v) => setLangField(["home", "benchmarkDesc"], v)}
        multiline
        rows={3}
      />
      <ListEditor
        label="Компании"
        items={tHome.companies}
        addLabel="Добавить компанию"
        fields={[
          { key: "name", label: "Название" },
          { key: "desc", label: "Описание" },
          { key: "tag", label: "Тег (например 'Tech')" },
        ]}
        onChange={(v) => setLangField(["home", "companies"], v)}
      />
    </div>
  );

  const renderWhy = () => (
    <div className="space-y-8">
      <SectionHeader title={`Почему мы — ${lang.toUpperCase()}`} />
      <TextField
        label="Overline"
        value={tHome.whyOverline}
        onChange={(v) => setLangField(["home", "whyOverline"], v)}
      />
      <TextField
        label="Заголовок"
        value={tHome.whyTitle}
        onChange={(v) => setLangField(["home", "whyTitle"], v)}
      />
      <StringListEditor
        label="Преимущества (список)"
        items={tHome.whyItems}
        onChange={(v) => setLangField(["home", "whyItems"], v)}
        addLabel="Добавить пункт"
      />

      <SectionHeader title={`Кто мы — ${lang.toUpperCase()}`} />
      <TextField
        label="Overline"
        value={tHome.whoOverline}
        onChange={(v) => setLangField(["home", "whoOverline"], v)}
      />
      <TextField
        label="Заголовок"
        value={tHome.whoTitle}
        onChange={(v) => setLangField(["home", "whoTitle"], v)}
      />
      <TextField
        label="Описание"
        value={tHome.whoDesc}
        onChange={(v) => setLangField(["home", "whoDesc"], v)}
        multiline
        rows={3}
      />
    </div>
  );

  const renderExperience = () => (
    <div className="space-y-8">
      <SectionHeader title={`Опыт — ${lang.toUpperCase()}`} />
      <TextField
        label="Overline"
        value={tHome.experienceOverline}
        onChange={(v) => setLangField(["home", "experienceOverline"], v)}
      />
      <TextField
        label="Заголовок"
        value={tHome.experienceTitle}
        onChange={(v) => setLangField(["home", "experienceTitle"], v)}
      />
      <ListEditor
        label="Карточки опыта"
        items={tHome.experienceItems}
        addLabel="Добавить"
        fields={[
          { key: "num", label: "Число (100, 40, 10+)" },
          { key: "label", label: "Подпись" },
        ]}
        onChange={(v) => setLangField(["home", "experienceItems"], v)}
      />

      <SectionHeader title={`Map settings ? ${lang.toUpperCase()}`} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <TextField label="Zoom (example: 1.2)" value={tHome.mapSettings?.zoom} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), zoom: v })} />
        <TextField label="Center X (longitude)" value={tHome.mapSettings?.centerX} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), centerX: v })} />
        <TextField label="Center Y (latitude)" value={tHome.mapSettings?.centerY} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), centerY: v })} />
        <TextField label="Layout X (%)" value={tHome.mapSettings?.layoutX} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), layoutX: v })} />
        <TextField label="Layout Y (%)" value={tHome.mapSettings?.layoutY} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), layoutY: v })} />
        <TextField label="Layout size (%)" value={tHome.mapSettings?.layoutSize} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), layoutSize: v })} />
        <TextField label="City label size" value={tHome.mapSettings?.cityLabelSize} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), cityLabelSize: v })} />
        <TextField label="City label color (#6fd6ff)" value={tHome.mapSettings?.cityLabelColor} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), cityLabelColor: v })} />
        <TextField label="Marker size" value={tHome.mapSettings?.cityMarkerSize} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), cityMarkerSize: v })} />
        <TextField label="Marker color (#ff6b35)" value={tHome.mapSettings?.cityMarkerColor} onChange={(v) => setLangField(["home", "mapSettings"], { ...(tHome.mapSettings || {}), cityMarkerColor: v })} />
      </div>

      <SectionHeader title={`CTA блок главной — ${lang.toUpperCase()}`} />
      <TextField
        label="Заголовок"
        value={tHome.ctaTitle}
        onChange={(v) => setLangField(["home", "ctaTitle"], v)}
      />
      <TextField
        label="Описание"
        value={tHome.ctaDesc}
        onChange={(v) => setLangField(["home", "ctaDesc"], v)}
        multiline
        rows={3}
      />
    </div>
  );

  const renderBusiness = () => (
    <div className="space-y-8">
      <SectionHeader title={`Бизнес-страница — ${lang.toUpperCase()}`} />
      <TextField label="Overline" value={tBus.overline} onChange={(v) => setLangField(["business", "overline"], v)} />
      <TextField label="Заголовок" value={tBus.title} onChange={(v) => setLangField(["business", "title"], v)} />
      <TextField label="Описание" value={tBus.desc} onChange={(v) => setLangField(["business", "desc"], v)} multiline rows={3} />
      <ListEditor
        label="Блоки услуг"
        items={tBus.blocks}
        addLabel="Добавить блок"
        fields={[{ key: "t", label: "Заголовок" }, { key: "d", label: "Описание" }]}
        onChange={(v) => setLangField(["business", "blocks"], v)}
      />
      <TextField label="Заголовок 'Примеры'" value={tBus.examplesTitle} onChange={(v) => setLangField(["business", "examplesTitle"], v)} />
      <StringListEditor
        label="Список компаний/направлений"
        items={tBus.examples}
        onChange={(v) => setLangField(["business", "examples"], v)}
        addLabel="Добавить"
      />
      <TextField label="CTA" value={tBus.cta} onChange={(v) => setLangField(["business", "cta"], v)} />
    </div>
  );

  const renderTravel = () => (
    <div className="space-y-8">
      <SectionHeader title={`Travel-страница — ${lang.toUpperCase()}`} />
      <TextField label="Overline" value={tTrv.overline} onChange={(v) => setLangField(["travel", "overline"], v)} />
      <TextField label="Заголовок" value={tTrv.title} onChange={(v) => setLangField(["travel", "title"], v)} />
      <TextField label="Описание" value={tTrv.desc} onChange={(v) => setLangField(["travel", "desc"], v)} multiline rows={3} />
      <ListEditor
        label="Города"
        items={tTrv.cities}
        addLabel="Добавить город"
        fields={[
          { key: "name", label: "Название (RU/EN)" },
          { key: "en", label: "ID (latin)" },
          { key: "desc", label: "Описание" },
        ]}
        onChange={(v) => setLangField(["travel", "cities"], v)}
      />
      <TextField label="CTA" value={tTrv.cta} onChange={(v) => setLangField(["travel", "cta"], v)} />
    </div>
  );

  const renderRetreat = () => (
    <div className="space-y-8">
      <SectionHeader title={`Retreat — ${lang.toUpperCase()}`} />
      <TextField label="Overline" value={tRtr.overline} onChange={(v) => setLangField(["retreat", "overline"], v)} />
      <TextField label="Заголовок" value={tRtr.title} onChange={(v) => setLangField(["retreat", "title"], v)} />
      <TextField label="Описание" value={tRtr.desc} onChange={(v) => setLangField(["retreat", "desc"], v)} multiline rows={3} />
      <ListEditor
        label="Блоки программы"
        items={tRtr.blocks}
        addLabel="Добавить"
        fields={[{ key: "t", label: "Заголовок" }, { key: "d", label: "Описание" }]}
        onChange={(v) => setLangField(["retreat", "blocks"], v)}
      />
      <TextField label="CTA" value={tRtr.cta} onChange={(v) => setLangField(["retreat", "cta"], v)} />
    </div>
  );

  const renderFilming = () => (
    <div className="space-y-8">
      <SectionHeader title={`Съёмки — ${lang.toUpperCase()}`} />
      <TextField label="Overline" value={tFlm.overline} onChange={(v) => setLangField(["filming", "overline"], v)} />
      <TextField label="Заголовок" value={tFlm.title} onChange={(v) => setLangField(["filming", "title"], v)} />
      <TextField label="Описание" value={tFlm.desc} onChange={(v) => setLangField(["filming", "desc"], v)} multiline rows={3} />
      <ListEditor
        label="Услуги"
        items={tFlm.blocks}
        addLabel="Добавить"
        fields={[{ key: "t", label: "Заголовок" }, { key: "d", label: "Описание" }]}
        onChange={(v) => setLangField(["filming", "blocks"], v)}
      />
      <ListEditor
        label="Cases: title + YouTube URL"
        items={tFlm.cases || DEFAULT_FILMING_CASES}
        addLabel="Add case"
        fields={[
          { key: "title", label: "Title" },
          { key: "youtubeUrl", label: "YouTube URL" },
        ]}
        onChange={(v) => setLangField(["filming", "cases"], v)}
      />
      <TextField label="CTA" value={tFlm.cta} onChange={(v) => setLangField(["filming", "cta"], v)} />
    </div>
  );

  const renderContacts = () => (
    <div className="space-y-8">
      <SectionHeader title={`Контакты — ${lang.toUpperCase()}`} />
      <TextField label="Overline" value={tCtc.overline} onChange={(v) => setLangField(["contacts", "overline"], v)} />
      <TextField label="Заголовок" value={tCtc.title} onChange={(v) => setLangField(["contacts", "title"], v)} />
      <TextField label="Описание" value={tCtc.desc} onChange={(v) => setLangField(["contacts", "desc"], v)} multiline rows={3} />
      <TextField label="Заголовок формы" value={tCtc.formTitle} onChange={(v) => setLangField(["contacts", "formTitle"], v)} />
    </div>
  );

  const renderFooter = () => (
    <div className="space-y-8">
      <SectionHeader title={`Footer — ${lang.toUpperCase()}`} />
      <TextField label="Tagline (под лого)" value={tFt.tagline} onChange={(v) => setLangField(["footer", "tagline"], v)} multiline />
      <TextField label="Текст 'Все права защищены'" value={tFt.rights} onChange={(v) => setLangField(["footer", "rights"], v)} />
      <TextField label="Заголовок 'Навигация'" value={tFt.pages} onChange={(v) => setLangField(["footer", "pages"], v)} />
      <TextField label="Заголовок 'Контакты'" value={tFt.contact} onChange={(v) => setLangField(["footer", "contact"], v)} />

      <SectionHeader title={`Навигация (меню) — ${lang.toUpperCase()}`} />
      {Object.keys(draft[lang]?.nav || {}).map((k) => (
        <TextField
          key={k}
          label={k}
          value={draft[lang].nav[k]}
          onChange={(v) => setLangField(["nav", k], v)}
        />
      ))}
    </div>
  );

  const renderVisibility = () => (
    <div className="space-y-3">
      <SectionHeader title="Видимость секций главной страницы" />
      <p className="text-white/55 text-sm mb-4">
        Включайте/выключайте секции, которые отображаются на главной.
      </p>
      {[
        ["home_popular", "Популярные туры"],
        ["home_inspire", "Travel and Inspire (видео)"],
        ["home_benchmark", "Бенчмарк компании"],
        ["home_why", "Почему мы / Кто мы"],
        ["home_map", "Карта Китая + Опыт"],
        ["home_cta", "CTA блок (форма) внизу главной"],
      ].map(([key, label]) => (
        <Toggle
          key={key}
          label={label}
          value={draft.visibility?.[key] !== false}
          onChange={(v) => setRoot("visibility", { ...draft.visibility, [key]: v })}
        />
      ))}
    </div>
  );

  const renderActiveSection = () => {
    switch (section) {
      case "branding": return renderBranding();
      case "home_hero": return renderHero();
      case "home_pillars": return renderPillars();
      case "home_popular": return renderPopular();
      case "home_inspire": return renderInspire();
      case "home_benchmark": return renderBenchmark();
      case "home_why": return renderWhy();
      case "home_experience": return renderExperience();
      case "business": return renderBusiness();
      case "travel": return renderTravel();
      case "retreat": return renderRetreat();
      case "filming": return renderFilming();
      case "contacts": return renderContacts();
      case "footer": return renderFooter();
      case "visibility": return renderVisibility();
      default: return null;
    }
  };

  const isLangSection = section !== "branding" && section !== "visibility";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Sidebar */}
      <aside className="lg:col-span-3">
        <div className="bg-midnight-2 border border-white/10 p-2 sticky top-20">
          <div className="overline px-3 pt-3 pb-2">Секции</div>
          {SECTION_KEYS.map((s) => (
            <button
              key={s.id}
              data-testid={`editor-section-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`w-full text-left px-3 py-2.5 font-display text-sm transition-colors ${
                section === s.id
                  ? "bg-coral text-midnight font-bold"
                  : "text-white/70 hover:text-white hover:bg-midnight-3"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </aside>

      {/* Main panel */}
      <div className="lg:col-span-9 space-y-6">
        {/* Top bar with lang switcher + save */}
        <div className="bg-midnight-2 border border-white/10 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 sticky top-20 z-10">
          <div className="flex items-center gap-3">
            <Globe size={16} className="text-coral" />
            {isLangSection ? (
              <div className="flex items-center gap-1 border border-white/15">
                {["ru", "en"].map((l) => (
                  <button
                    key={l}
                    data-testid={`editor-lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1.5 font-mono text-[11px] tracking-widest uppercase transition-colors ${
                      lang === l ? "bg-coral text-midnight" : "text-white/60 hover:text-white"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            ) : (
              <span className="font-mono text-[11px] tracking-widest uppercase text-white/55">
                Глобальная секция (без языка)
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="editor-reset"
              onClick={resetDefaults}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] tracking-widest uppercase hover:border-brand-red hover:text-brand-red transition-colors"
            >
              <RotateCcw size={13} /> Сброс
            </button>
            <button
              type="button"
              data-testid="editor-save"
              onClick={save}
              disabled={saving}
              className="btn-primary !py-2 !px-5 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
          </div>
        </div>

        <div className="bg-midnight-2 border border-white/10 p-6 md:p-8">
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
}
